/*
 * Copyright (c) 2026. Bolmate B.V.
 *
 * Generic action menu (the "⋯" menu on cards and rows): a trigger that opens a
 * body-portaled list of actions. Styled to match the PortalSelect dropdowns
 * (same shadow, radius, option metrics and hover) so selects and menus read as
 * one control family.
 */
import './style/bolmateMenu.scss';

import optionsHorizontal from '@iconify/icons-mi/options-horizontal';

import {Icon} from '@iconify/react';

import {remToPx} from '@dvrd/dvr-controls';

import classNames from 'classnames';

import React, {
    ForwardedRef,
    forwardRef,
    MouseEventHandler,
    ReactNode,
    useImperativeHandle,
    useMemo,
    useRef,
    useState
} from 'react';

import {usePortalDismiss} from '../usePortalDismiss';

import {createPortal} from 'react-dom';

export interface BolmateMenuOption {
    label: ReactNode;
    icon?: ReactNode;
    onClick: MouseEventHandler;
    disabled?: boolean;
    /** Renders the option in the destructive (red) style. */
    danger?: boolean;
}

export interface BolmateMenuRef {
    open: VoidFunction;
    close: VoidFunction;
}

interface Props {
    /** Menu options — pass a function to build them lazily on open (lists
     *  with many closed menus per row skip the per-render allocations). */
    options: BolmateMenuOption[] | (() => BolmateMenuOption[]);
    /** Custom trigger content — defaults to the horizontal-dots icon. */
    trigger?: ReactNode;
    className?: string;
    itemsClassName?: string;
    /** Preferred vertical side of the list relative to the trigger. */
    itemsPosition?: 'top' | 'bottom';
    /** Which trigger edge the list aligns to ('right' for right-edge menus). */
    align?: 'left' | 'right';
    shape?: 'square' | 'rounded';
    size?: 'small' | 'medium' | 'large';
    disabled?: boolean;
    portalTarget?: Element | string;
}

function BolmateMenu(props: Props, ref: ForwardedRef<BolmateMenuRef>) {
    const {
        options,
        trigger,
        className,
        itemsClassName,
        itemsPosition = 'bottom',
        align = 'left',
        shape = 'rounded',
        size = 'medium',
        disabled,
        portalTarget
    } = props;
    const [open, setOpen] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLDivElement>(null);
    const itemsRef = useRef<HTMLDivElement>(null);
    // Lazy options are only built while the menu is open.
    const resolvedOptions = useMemo(
        () => (typeof options === 'function' ? (open ? options() : []) : options),
        [options, open]
    );
    // Same fallback chain as PortalSelect: explicit element, selector, body.
    let target: Element;
    if (portalTarget === undefined) target = document.body;
    else if (typeof portalTarget === 'string') target = document.querySelector(portalTarget) ?? document.body;
    else target = portalTarget;

    useImperativeHandle(ref, () => ({
        open: () => setOpen(true),
        close: () => setOpen(false)
    }));

    function onToggleOpen(evt: React.MouseEvent) {
        // The trigger often sits inside a clickable card that opens this menu
        // itself — the toggle must not bubble into that handler.
        evt.stopPropagation();
        if (!disabled) setOpen(!open);
    }

    function onClickOption(option: BolmateMenuOption) {
        return function (evt: React.MouseEvent) {
            evt.stopPropagation();
            setOpen(false);
            option.onClick(evt);
        };
    }

    function setItemsPosition() {
        requestAnimationFrame(() => {
            const triggerElement = triggerRef.current;
            const items = itemsRef.current;
            if (!triggerElement || !items) return;
            const margin = remToPx(0.5);
            const gap = remToPx(0.25);
            const viewportWidth = document.documentElement.clientWidth;
            const viewportHeight = document.documentElement.clientHeight;
            const triggerRect = triggerElement.getBoundingClientRect();

            // Measure at natural size (viewport-capped) before choosing a side.
            items.style.setProperty('left', margin + 'px');
            items.style.setProperty('max-width', viewportWidth - 2 * margin + 'px');
            items.style.setProperty('max-height', 'none');
            const naturalRect = items.getBoundingClientRect();

            const spaceBelow = viewportHeight - triggerRect.bottom - gap - margin;
            const spaceAbove = triggerRect.top - gap - margin;
            let position = itemsPosition;
            if (position === 'bottom' && naturalRect.height > spaceBelow && spaceAbove > spaceBelow) position = 'top';
            else if (position === 'top' && naturalRect.height > spaceAbove && spaceBelow > spaceAbove)
                position = 'bottom';
            const space = Math.max(position === 'bottom' ? spaceBelow : spaceAbove, 0);
            items.style.setProperty('max-height', space + 'px');

            const itemsRect = items.getBoundingClientRect();
            let top: number;
            if (position === 'bottom') top = triggerRect.bottom + gap;
            else top = triggerRect.top - gap - itemsRect.height;
            top = Math.max(Math.min(top, viewportHeight - margin - itemsRect.height), margin);
            let left = align === 'right' ? triggerRect.right - itemsRect.width : triggerRect.left;
            left = Math.max(Math.min(left, viewportWidth - margin - itemsRect.width), margin);
            items.style.setProperty('top', top + 'px');
            items.style.setProperty('left', left + 'px');
        });
    }

    function renderTrigger() {
        return (
            <div
                className={classNames('bolmate-menu-trigger', open && 'open', disabled && 'disabled')}
                onClick={onToggleOpen}
                ref={triggerRef}
            >
                {trigger ?? <Icon icon={optionsHorizontal} className='bolmate-menu-trigger-icon' />}
            </div>
        );
    }

    function renderItems() {
        if (!open) return null;
        return createPortal(
            <div
                className={classNames('bolmate-menu-items', open && 'open', shape, `size-${size}`, itemsClassName)}
                ref={itemsRef}
                onMouseEnter={() => {
                    hoveringItems.current = true;
                }}
                onMouseLeave={() => {
                    hoveringItems.current = false;
                }}
            >
                {resolvedOptions.map(renderOption)}
            </div>,
            target
        );
    }

    function renderOption(option: BolmateMenuOption, index: number) {
        return (
            <div
                key={index}
                className={classNames('bolmate-menu-item', option.disabled && 'disabled', option.danger && 'danger')}
                onClick={option.disabled ? undefined : onClickOption(option)}
            >
                {option.icon != null && <span className='bolmate-menu-item-icon'>{option.icon}</span>}
                {React.isValidElement(option.label) ? (
                    option.label
                ) : (
                    <label className='bolmate-menu-item-label'>{option.label}</label>
                )}
            </div>
        );
    }

    const hoveringItems = usePortalDismiss({
        open,
        onClose: () => setOpen(false),
        onReposition: setItemsPosition,
        insideRefs: [rootRef, itemsRef],
        itemsRef
    });

    return (
        <div className={classNames('bolmate-menu', disabled && 'disabled', className)} ref={rootRef}>
            {renderTrigger()}
            {renderItems()}
        </div>
    );
}

export default forwardRef<BolmateMenuRef, Props>(BolmateMenu);
